import { useEffect } from 'react';

export const MENU_BUTTON_SIZE = 44;
const FLOATING_TOP = 12;
const HERO_INSET = 14;

export type MenuDock = { docked: boolean; top: number; right: number };

/**
 * Where the fixed menu button should sit. While the hero is on screen the
 * button rides inside it, vertically centered near its right edge; once the
 * hero scrolls up far enough, it stops at the floating position.
 */
export const getMenuDock = (
  hero: { top: number; bottom: number; right: number } | null,
  viewportWidth: number
): MenuDock | null => {
  if (!hero) return null;
  const right = Math.max(FLOATING_TOP, viewportWidth - hero.right + HERO_INSET);
  const centeredTop = hero.top + (hero.bottom - hero.top - MENU_BUTTON_SIZE) / 2;
  if (centeredTop >= FLOATING_TOP) {
    return { docked: true, top: centeredTop, right };
  }
  return { docked: false, top: FLOATING_TOP, right };
};

/**
 * Keeps the hamburger (react-burger-menu portals it to a fixed position)
 * aligned with the Lounge hero. Sets `body.mw-menu-docked` plus
 * `--mw-menu-top` / `--mw-menu-right`; CSS in lounge-modern.css does the rest.
 */
export const useHeroMenuDock = (enabled: boolean, routeKey: string) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const body = document.body;
    let frame = 0;

    const update = () => {
      frame = 0;
      const heroEl = document.querySelector('.mw-lounge-modern .mw-hero');
      const dock = getMenuDock(
        heroEl ? heroEl.getBoundingClientRect() : null,
        window.innerWidth
      );
      if (!dock) {
        body.classList.remove('mw-menu-docked', 'mw-menu-has-hero');
        return;
      }
      body.classList.add('mw-menu-has-hero');
      body.classList.toggle('mw-menu-docked', dock.docked);
      body.style.setProperty('--mw-menu-top', `${Math.round(dock.top)}px`);
      body.style.setProperty('--mw-menu-right', `${Math.round(dock.right)}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    // The hero can mount after route data loads; re-check shortly after.
    const settle = window.setTimeout(update, 400);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      window.clearTimeout(settle);
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      body.classList.remove('mw-menu-docked', 'mw-menu-has-hero');
      body.style.removeProperty('--mw-menu-top');
      body.style.removeProperty('--mw-menu-right');
    };
  }, [enabled, routeKey]);
};
